import PropTypes from 'prop-types';
import * as React from 'react';

import { Cell, Column, Table } from 'fixed-data-table-2';

import { useDispatch } from 'react-redux';
import { sortG5Devices } from '../../../actions/DeviceConfigurationAction';
import UIDateFormater, {
  DateType,
} from '../../../containers/UIhelper/UIDateFormater';
import SortableHeaderCell from '../../SortableHeaderCell/SortableHeaderCell';

import RefreshIcon from '@mui/icons-material/Refresh';
import { Button } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import useProjectTimezone from '../../../hooks/useProjectTimeZone';
import {
  buildG5ConfigItems,
  formatG5ConfigAudioReportValue,
  formatG5ConfigSchedulerRefTimeValue,
  formatG5ConfigSecondKeepaliveValue,
  formatG5ConfigServerValue,
  G5_CONFIG_UI_COLUMNS,
} from './g5ConfigColumns';

function ConfigsTable(props, context) {
  const { items, sort, local, uiInfo, options } = props;
  const dispatch = useDispatch();
  const projectTimeZone = useProjectTimezone();

  const [selectedItemIndex, setSelectedItemIndex] = React.useState(-1);
  const parsedItems = buildG5ConfigItems(items, options);

  const [preFilterItems, setPreFilterItems] = React.useState([]);
  const lastSelectedIndex = React.useRef();

  const filterCheckboxRender = (item, index) => (
    <Cell>
      <input
        type='checkbox'
        checked={preFilterItems.indexOf(item.DeviceID) !== -1}
        onChange={(event) => {
          const { checked } = event.target;
          const { shiftKey } = event.nativeEvent;
          let preFiltersArr = [...preFilterItems];
          if (checked) {
            if (!shiftKey) {
              preFiltersArr.push(item.DeviceID);
            } else {
              const start = Math.min(index, lastSelectedIndex.current);
              const end = Math.max(index, lastSelectedIndex.current);
              const multiSelect = parsedItems
                .filter(
                  (_item, itemIndex) => itemIndex >= start && itemIndex <= end
                )
                .map((item) => item.DeviceID);
              preFiltersArr.push(...multiSelect);
            }
          } else {
            if (!shiftKey) {
              // unchecked selected
              preFiltersArr = preFiltersArr.filter(
                (itemDeviceId) => itemDeviceId !== item.DeviceID
              );
            } else {
              const start = Math.min(index, lastSelectedIndex.current);
              const end = Math.max(index, lastSelectedIndex.current);
              const multiSelect = parsedItems
                .filter(
                  (_item, itemIndex) => itemIndex >= start && itemIndex <= end
                )
                .map((item) => item.DeviceID);
              preFiltersArr = preFiltersArr.filter(
                (itemDeviceId) => !multiSelect.includes(itemDeviceId)
              );
            }
          }
          setPreFilterItems(preFiltersArr);

          lastSelectedIndex.current = index;
        }}
      />
    </Cell>
  );

  const handleRowClick = (event, rowIndex) => {
    setSelectedItemIndex(rowIndex);
  };

  const rowClassNameGetter = (rowIndex) => {
    return rowIndex === selectedItemIndex ? 'active-row' : null;
  };

  const sortTable = {
    sortDir: sort.direction,
    sortBy: sort.field,
    sort: (columnKey) => {
      dispatch(sortG5Devices(columnKey));
      setSelectedItemIndex(-1);
    },
  };

  const renderConfigCell = (column, columnProps) => {
    const item = parsedItems[columnProps.rowIndex];

    switch (column.type) {
      case 'deviceType':
        return <Cell>{item.DeviceType}</Cell>;
      case 'lastStatusTime': {
        const time = UIDateFormater(
          item[column.key],
          DateType.DATE_AND_TIME,
          local,
          projectTimeZone
        );
        return <Cell>{time}</Cell>;
      }
      case 'schedulerRefTime':
        return <Cell>{formatG5ConfigSchedulerRefTimeValue(item)}</Cell>;
      case 'server':
        return <Cell>{formatG5ConfigServerValue(item[column.key])}</Cell>;
      case 'valueOrDash':
        return <Cell>{item[column.key] == null ? '--' : item[column.key]}</Cell>;
      case 'audioReportEnabled':
        switch (item[column.key]) {
          case 0:
            return <Cell>&#x2718;</Cell>;
          case 1:
            return <Cell>&#x2714;</Cell>;
          case 2:
            return <Cell>&#x2714; &#x2714;</Cell>;
          default:
            return <Cell>{formatG5ConfigAudioReportValue(item[column.key])}</Cell>;
        }
      case 'secondKeepalive':
        return <Cell>{formatG5ConfigSecondKeepaliveValue(item[column.key])}</Cell>;
      default:
        return <Cell>{item[column.key]}</Cell>;
    }
  };

  const getMultiRowsBySelection = (index) => {
    const start = Math.min(index, lastSelectedIndex.current);
    const end = Math.max(index, lastSelectedIndex.current);
    const multiSelect = items
      .filter((_item, itemIndex) => itemIndex >= start && itemIndex <= end)
      .map((item) => item.DeviceID);
    return multiSelect;
  };

  return (
    <section>
      <Table
        rowsCount={parsedItems.length}
        width={uiInfo.pageWidth / 1.1635}
        maxHeight={uiInfo.pageHeight / 1.0909}
        headerHeight={50}
        rowHeight={40}
        onRowClick={handleRowClick}
        rowClassNameGetter={rowClassNameGetter}
      >
        <Column
          fixed
          cellClassName='fixed-table-cell-class'
          align='center'
          columnKey=''
          width={60}
          header={
            <Cell>
              <Button
                variant='contained'
                onClick={() => {
                  props.handleSelection(preFilterItems);
                  setPreFilterItems([]);
                }}
              >
                {context.t('filter')}
              </Button>
            </Cell>
          }
          cell={(props) =>
            filterCheckboxRender(parsedItems[props.rowIndex], props.rowIndex)
          }
        />
        <Column
          fixed
          cellClassName='fixed-table-cell-class'
          align='center'
          columnKey='#'
          width={40}
          header={<Cell>#</Cell>}
          cell={(props) => <Cell>{props.rowIndex + 1}</Cell>}
        />
        {G5_CONFIG_UI_COLUMNS.map((column, index) => (
          <Column
            key={column.key}
            fixed={index < 4}
            cellClassName={index < 4 ? 'fixed-table-cell-class' : undefined}
            align='center'
            columnKey={column.key}
            width={column.width}
            header={
              column.key === 'DeviceType' ? (
                <Cell>{context.t('device_type') || column.title}</Cell>
              ) : (
                <SortableHeaderCell sortTable={sortTable} />
              )
            }
            cell={(columnProps) => renderConfigCell(column, columnProps)}
          />
        ))}
      </Table>

      <footer style={{ backgroundColor: 'white' }}>
        <IconButton onClick={props.reloadDevices}>
          <RefreshIcon fontSize='small' />
        </IconButton>

        <p style={{ float: 'right', margin: '5px 10px' }}>
          <span>
            {context.t('selected_param', {
              selectedItems: selectedItemIndex + 1,
            })}
          </span>
          &emsp;
          <span>
            {parsedItems.length} {context.t('items')}
          </span>
        </p>
      </footer>
    </section>
  );
}

ConfigsTable.contextTypes = {
  t: PropTypes.func.isRequired,
};

ConfigsTable.propTypes = {
  items: PropTypes.array.isRequired,
  local: PropTypes.string,
  reloadDevices: PropTypes.func.isRequired,
  selectedItems: PropTypes.array.isRequired,
  workingMode: PropTypes.string.isRequired,
};

export default ConfigsTable;
